import React from "react";
import { Button, Divider, Modal, Popconfirm, Space, Switch, Typography } from "antd";
import { CheckOutlined, CloseOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { Text } = Typography;

const MfaModal = ({
  visible,
  onCancel,
  user,
  mfaStatus,
  mfaStatusLoading,
  editableMfaRequired,
  handleToggleMfaRequired,
  mfaRequiredLoading,
  handleResetMfa,
  mfaLoading,
  loadMfaStatus,
}) => {
  const { t } = useTranslation();

  if (!user) return null;

  // Use API data if available, fallback to user object properties
  const mfaEnabled = mfaStatus?.totpEnabled ?? user.totpEnabled ?? false;
  const mfaSetupDate = mfaStatus?.totpSetupDate ?? user.totpSetupDate;
  const lastUsed = mfaStatus?.lastUsed;

  return (
    <Modal
      title={t("manage_mfa")}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          {t("close")}
        </Button>,
      ]}
      width={700}
      zIndex={1001}
    >
      <div>
        <Space direction="vertical" style={{ width: "100%" }} size={0}>
          {/* Current MFA Status */}
          <div>
            <Text strong style={{ fontSize: "16px", display: "block", marginBottom: "12px" }}>
              {t("current_mfa_status")}
            </Text>
            <Space direction="vertical" style={{ width: "100%" }} size={0}>
              <div style={{}}>
                <Text strong>{t("status")}: </Text>
                <Text type={mfaEnabled ? "success" : "secondary"}>
                  {mfaEnabled ? (
                    <>
                      <CheckOutlined style={{ color: "green", marginRight: 4 }} />
                      {t("enabled")}
                    </>
                  ) : (
                    <>
                      <CloseOutlined style={{ color: "red", marginRight: 4 }} />
                      {t("disabled")}
                    </>
                  )}
                </Text>
              </div>

              {mfaEnabled && mfaSetupDate && (
                <div style={{}}>
                  <Text strong>{t("setup_date")}: </Text>
                  <Text type="secondary">
                    {dayjs(mfaSetupDate.toDate ? mfaSetupDate.toDate() : mfaSetupDate).format("D MMM YYYY HH:mm")}
                  </Text>
                </div>
              )}

              {mfaEnabled && lastUsed && (
                <div style={{ padding: "8px 0" }}>
                  <Text strong>{t("last_used")}: </Text>
                  <Text type="secondary">
                    {dayjs(lastUsed.toDate ? lastUsed.toDate() : lastUsed).format("D MMM YYYY HH:mm")}
                  </Text>
                </div>
              )}

              {!mfaEnabled && (
                <div style={{ padding: "8px 0" }}>
                  <Text type="secondary" style={{ fontStyle: "italic" }}>
                    {t("mfa_not_setup_by_user_yet")}
                  </Text>
                </div>
              )}
            </Space>
            {/* Refresh Status Button */}
            <div style={{ margin: "8px 0" }}>
              <Button
                type="default"
                size="small"
                onClick={() => loadMfaStatus(user)}
                loading={mfaStatusLoading}
                icon={<SearchOutlined />}
              >
                {t("refresh_status")}
              </Button>
            </div>
          </div>

          <Divider />

          {/* MFA Requirement Toggle */}
          <div>
            <Text strong style={{ fontSize: "16px", display: "block", marginBottom: "12px" }}>
              {t("mfa_requirement_title")}
            </Text>
            <div>
              <div style={{ marginBottom: "12px" }}>
                <Text type="secondary">{t("mfa_requirement_description")}</Text>
              </div>
              <Space>
                <Switch
                  checked={editableMfaRequired}
                  onChange={handleToggleMfaRequired}
                  loading={mfaRequiredLoading}
                  checkedChildren={t("required")}
                  unCheckedChildren={t("optional")}
                />
              </Space>
              {editableMfaRequired && !mfaEnabled && (
                <div style={{ marginTop: "8px" }}>
                  <Text type="warning" style={{ fontSize: "12px" }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {t("user_must_setup_mfa_on_next_login")}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Reset MFA */}
          {mfaEnabled && (
            <>
              <Divider />
              <div>
                <Text strong style={{ fontSize: "16px", display: "block", marginBottom: "12px" }}>
                  {t("reset_mfa_title")}
                </Text>
                <div>
                  <div style={{ marginBottom: "12px" }}>
                    <Text type="secondary">{t("mfa_reset_description")}</Text>
                  </div>
                  <Popconfirm
                    title={t("mfa_reset_confirmation_title")}
                    description={t("mfa_reset_confirmation_description")}
                    onConfirm={handleResetMfa}
                    okText={t("mfa_reset_confirm_button")}
                    cancelText={t("cancel")}
                    okType="danger"
                  >
                    <Button type="primary" size="small" loading={mfaLoading} icon={<CloseOutlined />}>
                      {t("reset_mfa")}
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default MfaModal;
